import { useRef, useState } from "react";
import { AlertCircleIcon, CheckCircleIcon, HelpCircleIcon, MoreVerticalIcon } from "lucide-react";
import { UAParser } from "ua-parser-js";
import { Button } from "@component/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { Textarea } from "@component/textarea";
import { BugReportDevice, BugReportRequest, BugReportType, makeBugReport } from "@service/bugs";
import { catchErrorLog } from "@util/sentry";

interface IssuesDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (issueType: string, details: string) => void;
    userId: string;
    assessmentId?: string;
    colorScheme?: 'default' | 'black'; // Color scheme for different interview types
    /** Currently-selected mic device id (matches feedback-popup); falls back to first audioinput device. */
    selectedAudioDeviceID?: string;
    /** Currently-selected camera device id (matches feedback-popup); falls back to first videoinput device. */
    selectedVideoDeviceID?: string;
    /**
     * Where the dialog was triggered from — included in the bug report meta
     * so the BE/Sentry can distinguish reports filed in the waiting room vs.
     * during an active interview session.
     */
    type?: BugReportType;
    /** Swaps "interview" copy for "roleplay" throughout the dialog. */
    context?: "interview" | "roleplay";
    /** Override the default makeBugReport call — used by training flow which has no auth cookie. */
    makeReport?: (data: BugReportRequest) => Promise<{ success: boolean; message?: string }>;
}

export default function IssuesDialog({
    isOpen,
    onClose,
    onSubmit,
    userId,
    assessmentId,
    colorScheme = 'default',
    selectedAudioDeviceID,
    selectedVideoDeviceID,
    type,
    context = "interview",
    makeReport,
}: IssuesDialogProps) {
    const s = context === "roleplay" ? "roleplay" : "interview";
    // Color scheme configuration
    const isBlackScheme = colorScheme === 'black';
    const colorClasses = isBlackScheme ? {
        iconBg: 'bg-black',
        iconColor: 'text-white',
        primaryButton: 'bg-black hover:bg-gray-800 text-white',
        secondaryButton: 'border-black text-black hover:bg-black hover:text-white',
        successIconBg: 'bg-black',
        successIconColor: 'text-white'
    } : {
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-500',
        primaryButton: 'bg-blue-600 hover:bg-blue-700',
        secondaryButton: '',
        successIconBg: 'bg-green-100',
        successIconColor: 'text-green-600'
    };

    const [issueType, setIssueType] = useState<string>("");
    const [details, setDetails] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    // Synchronous guard: blocks a second submit before React paints the
    // disabled state from `isSubmitting`, so rapid double-clicks can't
    // fire two `makeBugReport` calls.
    const submitInFlightRef = useRef(false);

    // Categories where a written reason is required so support can act on the report.
    const ISSUE_TYPES_REQUIRING_DETAILS = ["camera-mic", "connection", "other"] as const;
    const detailsRequired = (ISSUE_TYPES_REQUIRING_DETAILS as readonly string[]).includes(issueType);
    const detailsMissing = detailsRequired && details.trim().length === 0;

    // Per-category example prompts for the details box. Giving the user a
    // concrete example of what to write means support receives actionable
    // reports instead of empty or one-word descriptions.
    const ISSUE_DETAIL_PLACEHOLDERS: Record<string, string> = {
        "camera-mic":
            "e.g. I allowed camera & microphone access but my camera still shows a black screen / my mic isn't picking up my voice.",
        "connection":
            "e.g. The interview keeps freezing and reconnecting, and my video drops out every few seconds.",
        "other":
            `e.g. The ${s} ended unexpectedly after the second question and I couldn't continue.`,
    };

    const handleSubmit = async () => {
        if (!issueType) return;
        if (detailsMissing) return;
        if (submitInFlightRef.current) return;
        submitInFlightRef.current = true;

        setIsSubmitting(true);
        setSubmitError(null);

        // Wrap the whole flow so the user is never stuck with a spinning
        // submit button — any unexpected throw (enumerateDevices on hostile
        // origins, UAParser, axios rejection, etc.) must still release
        // `isSubmitting`.
        try {
            // Mirror feedback-popup's metadata collection so bug reports carry
            // the same device/browser context for cross-referencing in
            // Sentry/Mixpanel. Two gotchas worth keeping in mind:
            //  - Browsers expose `deviceId === "default"` for the OS default
            //    MIC, but not for the default CAMERA — falling through to the
            //    first `videoinput` keeps `video_device` populated when the
            //    caller didn't pass a specific id.
            //  - `MediaDeviceInfo` properties live on the prototype and are
            //    non-enumerable in many browsers, so `JSON.stringify(device)`
            //    can produce `{}`. `toPlain()` copies the fields onto a POJO
            //    matching `BugReportDevice` so the BE actually receives them.
            const toPlain = (d: MediaDeviceInfo | undefined): BugReportDevice | undefined =>
                d
                    ? {
                          deviceId: d.deviceId,
                          kind: d.kind,
                          label: d.label,
                          groupId: d.groupId,
                      }
                    : undefined;

            let currentAudioInput: MediaDeviceInfo | undefined;
            let currentVideoInput: MediaDeviceInfo | undefined;
            try {
                const listDevices = await navigator.mediaDevices.enumerateDevices();
                const audioInputDevices = listDevices.filter((d) => d.kind === "audioinput");
                const videoInputDevices = listDevices.filter((d) => d.kind === "videoinput");
                // Strict equality — `.includes()` was wrong here: device IDs
                // are long opaque hashes and one ID can be a substring of
                // another, plus the previous default `"default"` would match
                // every Chrome OS-default mic regardless of the user's pick.
                // When no selection is passed, fall back to the first device
                // so the bug report still carries device context.
                currentAudioInput =
                    (selectedAudioDeviceID
                        ? audioInputDevices.find((d) => d.deviceId === selectedAudioDeviceID)
                        : undefined) || audioInputDevices[0];
                currentVideoInput =
                    (selectedVideoDeviceID
                        ? videoInputDevices.find((d) => d.deviceId === selectedVideoDeviceID)
                        : undefined) || videoInputDevices[0];
            } catch (err) {
                // enumerateDevices may throw under restrictive permissions or
                // on insecure origins; degrade gracefully and still ship the
                // bug report without device metadata.
                catchErrorLog(`IssuesDialog: enumerateDevices failed: ${err}`);
            }

            const { browser, cpu, device, os } = UAParser(navigator.userAgent);

            const bugReportData: BugReportRequest = {
                bug_type: issueType,
                bug_description: details,
                assessment_id: assessmentId,
                meta: {
                    user_id: userId,
                    ...(type ? { type } : {}),
                    browser: browser.name || "Unknown",
                    browser_version: browser.version || "Unknown",
                    cpu_architecture: cpu.architecture || "Unknown",
                    device_model: device.model || "Unknown",
                    device_vendor: device.vendor || "Unknown",
                    device_type: device.type || "Unknown",
                    os: os.name || "Unknown",
                    audio_device: toPlain(currentAudioInput),
                    video_device: toPlain(currentVideoInput),
                },
            };

            const response = makeReport
                ? await makeReport(bugReportData)
                : await makeBugReport(bugReportData);
            if (response.success) {
                onSubmit(issueType, details);
                handleClose();
            } else {
                setSubmitError("Failed to submit your report. Please try again.");
            }
        } catch (err) {
            catchErrorLog(`IssuesDialog: bug report submission failed: ${err}`);
            setSubmitError("Failed to submit your report. Please try again.");
        } finally {
            setIsSubmitting(false);
            submitInFlightRef.current = false;
        }
    };

    const handleClose = () => {
        setIssueType("");
        setDetails("");
        setIsSubmitted(false);
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md">
                {!isSubmitted ? (
                    <>
                        <DialogHeader className="flex flex-col items-center text-center space-y-4 pt-4">
                            <div className={`w-16 h-16 rounded-full ${colorClasses.iconBg} flex items-center justify-center`}>
                                <AlertCircleIcon className={`h-8 w-8 ${colorClasses.iconColor}`} />
                            </div>
                            <DialogTitle className="text-xl font-semibold">
                                Having issues with the {s}?
                            </DialogTitle>
                            <p className="text-muted-foreground px-4">
                                Please select the issue you're experiencing and provide any
                                additional details. Our team will review your report and help
                                you resolve the issue.
                            </p>
                        </DialogHeader>

                        <div className="space-y-4 py-2">
                            <Select value={issueType} onValueChange={setIssueType}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select an issue type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="just-not-ready">
                                        Just Not Ready
                                    </SelectItem>
                                    <SelectItem value="camera-mic">
                                        Camera/Microphone Permission Issues
                                    </SelectItem>
                                    <SelectItem value="browser-compatibility">
                                        Browser Compatibility Issues
                                    </SelectItem>
                                    <SelectItem value="connection">
                                        Internet Connection Problems
                                    </SelectItem>
                                    <SelectItem value="loading">{context === "roleplay" ? "Roleplay" : "Interview"} Not Loading</SelectItem>
                                    <SelectItem value="other">Other Issue</SelectItem>
                                </SelectContent>
                            </Select>

                            {issueType === "camera-mic" && (
                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md px-3 py-2.5">
                                    <div className="flex items-start gap-2">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-1.5">
                                                Allow camera &amp; microphone access
                                            </p>
                                            <div className="text-xs text-blue-900 dark:text-blue-100 leading-relaxed space-y-1">
                                                <div className="flex gap-1.5">
                                                    <span className="font-medium tabular-nums shrink-0">1.</span>
                                                    <span className="flex-1">
                                                        <span className="sm:hidden">
                                                            Tap the
                                                            <span className="inline-flex items-center justify-center align-middle mx-1 w-5 h-5 rounded bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200">
                                                                <MoreVerticalIcon className="w-3.5 h-3.5" />
                                                            </span>
                                                            next to the address bar, then open <strong>Settings</strong>.
                                                        </span>
                                                        <span className="hidden sm:inline">Click the lock or settings icon next to the address bar.</span>
                                                    </span>
                                                </div>
                                                <div className="flex gap-1.5">
                                                    <span className="font-medium tabular-nums shrink-0">2.</span>
                                                    <span className="flex-1">Allow both <strong>Camera</strong> and <strong>Microphone</strong> for this site.</span>
                                                </div>
                                                <div className="flex gap-1.5">
                                                    <span className="font-medium tabular-nums shrink-0">3.</span>
                                                    <span className="flex-1">Refresh the page to retry the {s}.</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-1">
                                <Textarea
                                    placeholder={
                                        ISSUE_DETAIL_PLACEHOLDERS[issueType] ??
                                        (detailsRequired
                                            ? "Please describe your issue in detail..."
                                            : "Please provide additional details about your issue...")
                                    }
                                    value={details}
                                    onChange={(e: any) => setDetails(e.target.value)}
                                    className={`min-h-[100px] ${detailsMissing ? "border-red-400 focus-visible:ring-red-400" : ""}`}
                                />
                                {detailsRequired && (
                                    <p className={`text-xs ${detailsMissing ? "text-red-600" : "text-muted-foreground"}`}>
                                        Please provide a clear reason so our team can investigate and help resolve your issue.
                                    </p>
                                )}
                            </div>
                        </div>

                        {submitError && (
                            <p className="text-sm text-red-500 text-center -mb-1">
                                {submitError}
                            </p>
                        )}

                        <DialogFooter className="gap-3">
                            <Button
                                variant="outline"
                                onClick={handleClose}
                                className={`w-full sm:w-auto ${colorClasses.secondaryButton}`}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className={`w-full sm:w-auto ${colorClasses.primaryButton} cursor-pointer`}
                                onClick={handleSubmit}
                                disabled={!issueType || detailsMissing || isSubmitting}
                            >
                                {isSubmitting ? "Submitting..." : "Submit Report"}
                            </Button>
                        </DialogFooter>
                    </>
                ) : (
                    <>
                        <DialogHeader className="flex flex-col items-center text-center space-y-4 pt-4">
                            <div className={`w-16 h-16 rounded-full ${colorClasses.successIconBg} flex items-center justify-center`}>
                                <CheckCircleIcon className={`h-8 w-8 ${colorClasses.successIconColor}`} />
                            </div>
                            <DialogTitle className="text-xl font-semibold">
                                Report Submitted
                            </DialogTitle>
                            <p className="text-muted-foreground px-4">
                                Thank you for reporting the issue. Our team will review your
                                report and get back to you shortly. You can now request to
                                retake the {s}.
                            </p>
                        </DialogHeader>

                        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg my-2">
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                Report ID:{" "}
                                <span className="font-mono">
                                    {Math.random().toString(36).substring(2, 10).toUpperCase()}
                                </span>
                            </p>
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                className={`w-full sm:w-auto ${colorClasses.primaryButton}`}
                                onClick={handleClose}
                            >
                                Close
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}